import { z } from "zod";
import { createRouter, mutation, query } from "react-safe-query";
import { AppType } from "@utils/constants";

const V3_API_BASE_URL = "https://api.v3.invintusmedia.com";
const V3_CRUD_API_BASE_URL = "https://crud.v3.invintusmedia.com";
const WSC_API_KEY = "WTw0fEUjXau3J5e79L6iycALE2456sRef9i0vw5kU";

declare module GetClientAppsResponse {
  export interface Manifest {
    createdAt: string;
    updatedAt: string;
    manifestID: string;
    data: any;
    apIdx: string;
  }

  export interface RootObject {
    appID: string;
    appName: string;
    manifest: Manifest;
    targets: AppType[];
  }
}

const createAppPayloadSchema = z.object({
  clientID: z.string(),
  appName: z.string(),
  manifest: z.any(),
  targets: z.array(z.string()),
});

const updateAppPayloadSchema = z.object({
  clientID: z.string(),
  appName: z.string(),
  appID: z.string(),
  manifest: z.any(),
  targets: z.array(z.string()),
});

const updateAppNameAndTargetsPayloadSchema = z.object({
  clientID: z.string(),
  appName: z.string(),
  appID: z.string(),
  targets: z.array(z.string()),
});

const updateAppStatusPayloadSchema = z.object({
  clientID: z.string(),
  appID: z.string(),
  status: z.string(),
});

const deleteAppPayloadSchema = z.object({
  clientID: z.string(),
  appID: z.number(),
});

const getAppByIDPayloadSchema = z.object({
  clientID: z.string(),
  appID: z.string(),
});

const getVideosByCategoryIDPayloadSchema = z.object({
  clientID: z.string(),
  categoryID: z.string(),
});

const buildAppPayloadSchema = z.object({
  clientID: z.string(),
  appID: z.string(),
  targets: z.array(z.string()),
  formData: z.any(),
});

const generalSearchPayloadSchema = z.object({
  clientID: z.string(),
  resultMax: z.number().optional(),
  resultPage: z.number().optional(),
  showAudioDownload: z.boolean().optional(),
  showRuntime: z.boolean().optional(),
  sortBy: z.string(),
  startDateTime: z.string().optional(),
  status: z.string(),
  stopDateTime: z.string().optional(),
  terms: z.string(),
});

const getClientAppsPayloadSchema = z.object({
  clientID: z.string(),
});

export const ottRouter = createRouter({
  createApp: mutation(async (input: z.infer<typeof createAppPayloadSchema>) => {
    const response = await fetch(`${V3_CRUD_API_BASE_URL}/Clientdeliveryapp/createApp`, {
      method: "POST",
      headers: {
        authorization: "console",
        "wsc-api-key": WSC_API_KEY,
      },
      body: JSON.stringify({
        clientID: input.clientID,
        appName: input.appName,
        manifest: input.manifest,
        targets: input.targets,
      }),
    });

    return await response.json().then((json) => json.data);
  }),
  updateAppNameAndTargets: mutation(async (input: z.infer<typeof updateAppNameAndTargetsPayloadSchema>) => {
    const response = await fetch(`${V3_CRUD_API_BASE_URL}/Clientdeliveryapp/updateApp`, {
      method: "POST",
      headers: {
        authorization: "console",
        "wsc-api-key": WSC_API_KEY,
      },
      body: JSON.stringify({
        appID: input.appID,
        clientID: input.clientID,
        appName: input.appName,
        targets: input.targets,
      }),
    });

    return await response.json().then((json) => json.data);
  }),

  updateApp: mutation(async (input: z.infer<typeof updateAppPayloadSchema>) => {
    const response = await fetch(`${V3_CRUD_API_BASE_URL}/Clientdeliveryapp/updateApp`, {
      method: "POST",
      headers: {
        authorization: "console",
        "wsc-api-key": WSC_API_KEY,
      },
      body: JSON.stringify({
        appID: input.appID,
        clientID: input.clientID,
        appName: input.appName,
        manifest: input.manifest,
        targets: input.targets,
      }),
    });

    return await response.json().then((json) => json.data);
  }),
  deleteApp: mutation(async (input: z.infer<typeof deleteAppPayloadSchema>) => {
    const response = await fetch(`${V3_CRUD_API_BASE_URL}/Clientdeliveryapp/deleteApp`, {
      method: "POST",
      headers: {
        authorization: "console",
        "wsc-api-key": WSC_API_KEY,
      },
      body: JSON.stringify({
        clientID: input.clientID,
        appID: input.appID,
      }),
    });

    return response;
  }),
  getAppByID: query(async (input: z.infer<typeof getAppByIDPayloadSchema>) => {
    const response = await fetch(`${V3_API_BASE_URL}/v2/OttApps/getApps`, {
      method: "POST",
      headers: {
        authorization: "console",
        "wsc-api-key": WSC_API_KEY,
      },
      body: JSON.stringify({
        clientID: input.clientID,
        appID: input.appID,
      }),
    });

    return await response.json().then((json) => json.data[0]);
  }),
  getVideosByCategoryID: query(async (input: z.infer<typeof getVideosByCategoryIDPayloadSchema>) => {
    const response = await fetch(`${V3_API_BASE_URL}/v2/MobileApp/getAppCategoryEvents`, {
      method: "POST",
      headers: {
        authorization: "console",
        "wsc-api-key": WSC_API_KEY,
      },
      body: JSON.stringify(input),
    });

    return await response.json().then((json) => json.data);
  }),
  generalSearch: query(async (input: z.infer<typeof generalSearchPayloadSchema>) => {
    const response = await fetch(`${V3_API_BASE_URL}/v2/Search/general`, {
      method: "POST",
      headers: {
        authorization: "console",
        "wsc-api-key": WSC_API_KEY,
      },
      body: JSON.stringify(input),
    });

    return await response.json().then((json) => json.data);
  }),
  getClientApps: query(async (input: z.infer<typeof getClientAppsPayloadSchema>) => {
    const response = await fetch(`${V3_API_BASE_URL}/v2/OttApps/getApps`, {
      method: "POST",
      headers: {
        authorization: "console",
        "wsc-api-key": WSC_API_KEY,
      },
      body: JSON.stringify({
        clientID: input.clientID,
      }),
    });

    return await response.json().then((json) => json.data as GetClientAppsResponse.RootObject[]);
  }),
  // Build Single Curl Call
  // curl -X POST http://localhost:3000/app/build/1000000000/42
  buildApp: mutation(async (input: z.infer<typeof buildAppPayloadSchema>) => {
    const response = await fetch(`${process.env.REACT_APP_DEPLOYMENT_SERVER_URL}/app/build/${input.clientID}/${input.appID}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${process.env.REACT_APP_DEPLOYMENT_SERVER_API_KEY}`,
      },
      body: input.formData,
    });

    return await response.json().then((json) => json.data);
  }),
});
